"use client"

import type {
  ComponentPropsWithoutRef,
  ComponentPropsWithRef,
  CSSProperties,
  Ref,
  SyntheticEvent,
} from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import cn from "classnames"

import { mergeRefs } from "~/functions/mergeRefs"
import { parseVimeoMessage } from "~/functions/parseVimeoMessage"
import { postMessageToVimeo } from "~/functions/postMessageToVimeo"
import { useOnScreen } from "~/hooks/useOnScreen"
import type { Embed as EmbedType } from "~/types/asset"

import type { SharedAssetProps } from "./Asset"
import s from "./Asset.module.scss"
import { Image } from "./Image"

export type EmbedProps = {
  ref?: Ref<HTMLSpanElement>
  embed: EmbedType
  embedProps?: Omit<ComponentPropsWithRef<"iframe">, "src" | "onLoad">
  onLoad?: (element: HTMLIFrameElement | HTMLImageElement) => void
  useIframeAspectRatio?: boolean
} & Omit<ComponentPropsWithoutRef<"span">, "onLoad"> &
  SharedAssetProps

export const Embed = ({
  ref,
  embed,
  embedProps,
  onLoad: propOnLoad,
  hasPriority = false,
  loading = "lazy",
  objectFit = "cover",
  animateOnLoad: animateOnLoadProp,
  useIframeAspectRatio = false,
  lazyLoadBackgroundColor,
  ...rest
}: EmbedProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLSpanElement>(null)
  // handleMessage is stable, so it can't read videoPlaying to dedupe the
  // ~4/sec timeupdate stream - a ref is the only thing it can see
  const playbackStartedRef = useRef(false)
  const embedUrl = `${embed.url}?background=1&dnt=1`
  const [iframeReady, setIframeReady] = useState(false)
  const [loadVideo, setLoadVideo] = useState(loading === "eager" || false)
  const [videoPlaying, setVideoPlaying] = useState(false)
  const [renderPoster, setRenderPoster] = useState(!!embed.poster)

  const onScreen = useOnScreen(containerRef, {
    rootMargin: "100px",
  })

  const handleMessage = useCallback((event: MessageEvent) => {
    // Security check - event.origin is always a bare scheme://host[:port], so
    // this must be an exact match to reject e.g. player.vimeo.com.example.com
    if (event.origin !== "https://player.vimeo.com") return

    const sender = iframeRef.current
    if (!sender) return
    if (sender.contentWindow !== event.source) return

    // The player posts objects, not JSON strings
    const data = parseVimeoMessage(event.data)
    if (!data) return

    // Subscribe to the playback events for this iframe once it's ready
    if (data.event === "ready") {
      setIframeReady(true)
      postMessageToVimeo(sender, "addEventListener", "play")
      // background=1 autoplays, so 'play' may already have fired before the
      // subscription above landed. 'timeupdate' fires throughout playback, so
      // it catches that case instead of leaving the poster stuck.
      postMessageToVimeo(sender, "addEventListener", "timeupdate")
    }

    // Once the video is actually playing, fade the poster out and stop
    // listening - timeupdate would otherwise keep firing several times a second
    if (data.event === "play" || data.event === "timeupdate") {
      if (playbackStartedRef.current) return
      playbackStartedRef.current = true

      setVideoPlaying(true)

      postMessageToVimeo(sender, "removeEventListener", "play")
      postMessageToVimeo(sender, "removeEventListener", "timeupdate")
    }
  }, [])

  useEffect(() => {
    if (videoPlaying) {
      const timeout = setTimeout(() => {
        setRenderPoster(false)
      }, 500) // Adjust the timeout duration as needed
      return () => clearTimeout(timeout)
    }
  }, [videoPlaying])

  useEffect(() => {
    if (onScreen) {
      setLoadVideo(true)
    }
    if (iframeRef.current && iframeReady) {
      postMessageToVimeo(iframeRef.current, onScreen ? "play" : "pause")
    }
  }, [onScreen, handleMessage, iframeReady])

  useEffect(() => {
    if (loadVideo) {
      window.addEventListener("message", handleMessage)
    }

    return () => {
      window.removeEventListener("message", handleMessage)
    }
  }, [handleMessage, loadVideo])

  const onLoadIframe = useCallback(
    (event: SyntheticEvent<HTMLIFrameElement>) => {
      if (propOnLoad && !embed.poster) propOnLoad(event.currentTarget)

      const container = containerRef.current
      if (container) container.setAttribute("data-is-loaded", "true")
    },
    [propOnLoad, embed.poster],
  )

  const onLoadPoster = useCallback(
    (image: HTMLImageElement) => {
      if (propOnLoad) propOnLoad(image)
    },
    [propOnLoad],
  )

  const animateOnLoad = useMemo(() => {
    return hasPriority && animateOnLoadProp === undefined
      ? false
      : (animateOnLoadProp ?? true)
  }, [hasPriority, animateOnLoadProp])

  const mergedContainerRefs = useMemo(() => {
    return mergeRefs(containerRef, ref)
  }, [containerRef, ref])

  return (
    <span
      {...rest}
      ref={mergedContainerRefs}
      className={cn(s.assetContainer, rest.className)}
      style={
        {
          aspectRatio:
            objectFit === "contain" || useIframeAspectRatio
              ? `${embed.width} / ${embed.height}`
              : undefined,
          "--lazy-load-background-color": lazyLoadBackgroundColor,
          ...rest.style,
        } as CSSProperties
      }
      data-is-embed
      data-has-lazy-load-background-color={
        lazyLoadBackgroundColor !== false && animateOnLoad
      }
      data-video-playing={videoPlaying}
      {...(animateOnLoad && { "data-animate-on-load": true })}
    >
      {embed.poster && renderPoster && (
        <Image
          className={s.poster}
          image={embed.poster}
          imageSizes={{ base: "100vw" }}
          objectFit="cover"
          lazyLoadBackgroundColor={lazyLoadBackgroundColor}
          onLoad={onLoadPoster}
        />
      )}
      <iframe
        ref={iframeRef}
        title={embed.alt || "Embedded Content"}
        tabIndex={-1}
        // Here iframe props can only override the props above but not the
        // things after - this is deliberate
        {...embedProps}
        allow="autoplay; fullscreen"
        className={embedProps ? cn(s.asset, embedProps.className) : s.asset}
        style={{
          aspectRatio:
            objectFit === "cover"
              ? `${embed.width} / ${embed.height}`
              : undefined,
          ...(embedProps && embedProps.style),
        }}
        src={loadVideo ? embedUrl : undefined}
        onLoad={onLoadIframe}
      />
    </span>
  )
}
